import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

import { AuthSchemas } from "@/api/router/auth/schemas";
import { orpc } from "@/client";
import { Conversations } from "@/lib/conversation-session";
import { AuthLabels } from "@/lib/labels";

type LoginInput = typeof AuthSchemas.login.infer;

export function useLogin() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutateAsync } = useMutation(orpc.auth.login.mutationOptions());

	const methods = useForm<LoginInput>({
		resolver: arktypeResolver(AuthSchemas.login),
	});

	const onSubmit: SubmitHandler<LoginInput> = async (data) => {
		await mutateAsync(data)
			.then((user) => {
				Conversations.clear();
				queryClient.clear();
				if (user.must_change_password) {
					return navigate({ to: "/login", search: { change: true }, replace: true });
				}

				return navigate({ to: "/", replace: true });
			})
			.catch(() => {
				toast.error(AuthLabels.error("UNAUTHORIZED"));
			});
	};

	return {
		methods,
		handleSubmit: methods.handleSubmit(onSubmit),
	};
}
